// src/features/hotels/components/fields/HotelFields.tsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDataProvider, useNotify, useTranslate, SimpleForm, useRecordContext, LoadingIndicator } from 'react-admin';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    TextField,
    Switch,
    FormControlLabel,
    Alert,
    CircularProgress,
    Chip,
    Card,
    CardContent,
    IconButton,
    Grid,
    Tooltip,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    Settings as SettingsIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Warning as WarningIcon,
    ArrowUpward as MoveUpIcon,
    ArrowDownward as MoveDownIcon,
    TextFields as FieldIcon,
} from '@mui/icons-material';

// SYSTEM COMPONENTS
import { MyToolbar } from '../../../../components/forms/MyToolbar';
import { MyFormContainer } from '../../../../components/forms/MyFormContainer';
import { 
  MyFormLayout, 
  FormCard, 
  MyFormSection 
} from '../../../../components/forms/MyFormLayout';

import { useHotelPermissions } from '../../hooks/useHotelPermissions';
import { Hotel } from '../../types';

// Interface for reference fields according to backend
interface ReferenceFieldDto {
    key: string;
    label: string;
    isMandatory: boolean;
    displayOrder: number;
}

// Form interface
interface ReferenceFieldFormData {
    key: string;
    label: string;
    isMandatory: boolean;
    displayOrder: number;
}

/**
 * FIELD CARD COMPONENT
 * Displays individual field information in a card format
 */
const FieldCard: React.FC<{
    field: ReferenceFieldDto;
    index: number;
    totalFields: number;
    onEdit: (field: ReferenceFieldDto, index: number) => void;
    onDelete: (index: number) => void;
    onMoveUp: (index: number) => void;
    onMoveDown: (index: number) => void;
    canEdit: boolean;
    canDelete: boolean;
    translate: any;
}> = ({ field, index, totalFields, onEdit, onDelete, onMoveUp, onMoveDown, canEdit, canDelete, translate }) => {
    return (
        <Card 
            variant="outlined" 
            sx={{ 
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                transition: 'all 0.3s ease',
                '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }
            }}
        >
            <CardContent>
                <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12} sm={1}>
                        <Tooltip title={translate('resources.hotels.fields.display_order')}>
                            <Chip 
                                label={index + 1} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                            />
                        </Tooltip>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <FieldIcon color="action" fontSize="small" />
                            <Typography variant="subtitle1" fontWeight="medium">
                                {field.label}
                            </Typography>
                            {field.isMandatory && (
                                <Chip 
                                    label={translate('resources.hotels.fields.mandatory')} 
                                    size="small" 
                                    color="error" 
                                    variant="outlined"
                                />
                            )}
                        </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                            {translate('resources.hotels.fields.key')}: <code>{field.key}</code>
                        </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={3}>
                        <Box display="flex" justifyContent="flex-end" gap={0.5}>
                            {canEdit && (
                                <>
                                    <Tooltip title={translate('resources.hotels.fields.move_up')}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={() => onMoveUp(index)}
                                                disabled={index === 0}
                                                sx={{
                                                    '&:hover': {
                                                        backgroundColor: 'action.hover',
                                                    }
                                                }}
                                            >
                                                <MoveUpIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    
                                    <Tooltip title={translate('resources.hotels.fields.move_down')}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={() => onMoveDown(index)}
                                                disabled={index === totalFields - 1}
                                                sx={{
                                                    '&:hover': {
                                                        backgroundColor: 'action.hover',
                                                    }
                                                }}
                                            >
                                                <MoveDownIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    
                                    <Tooltip title={translate('resources.hotels.fields.edit')}>
                                        <IconButton
                                            size="small"
                                            onClick={() => onEdit(field, index)}
                                            color="primary"
                                            sx={{
                                                '&:hover': {
                                                    backgroundColor: 'primary.lighter',
                                                }
                                            }}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            )}
                            
                            {canDelete && (
                                <Tooltip title={translate('resources.hotels.fields.delete')}>
                                    <IconButton
                                        size="small"
                                        onClick={() => onDelete(index)}
                                        color="error"
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: 'error.lighter',
                                            }
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

const HotelFields: React.FC = () => {
    // React Admin hooks and navigation
    const { id: hotelId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const dataProvider = useDataProvider() as any;
    const notify = useNotify();
    const translate = useTranslate();
    const record = useRecordContext<Hotel>();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Local state
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hotel, setHotel] = useState<Hotel | null>(record || null);
    const [referenceFields, setReferenceFields] = useState<ReferenceFieldDto[]>([]);
    const [originalFields, setOriginalFields] = useState<ReferenceFieldDto[]>([]);
    const [hasChanges, setHasChanges] = useState(false);

    // Form state
    const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
    const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [fieldToDeleteIndex, setFieldToDeleteIndex] = useState<number | null>(null);

    // Field form
    const [fieldForm, setFieldForm] = useState<ReferenceFieldFormData>({
        key: '',
        label: '',
        isMandatory: false,
        displayOrder: 0,
    });

    // Permissions
    const permissions = useHotelPermissions(hotel);

    // Load initial data
    useEffect(() => {
        loadData();
    }, [hotelId]);

    // Update hotel from context
    useEffect(() => {
        if (record) {
            setHotel(record);
        }
    }, [record]);

    // Detect changes
    useEffect(() => {
        const fieldsChanged = JSON.stringify(referenceFields) !== JSON.stringify(originalFields);
        setHasChanges(fieldsChanged);
    }, [referenceFields, originalFields]);

    /**
     * Load hotel and field configuration data
     */
    const loadData = async () => {
        if (!hotelId) return;

        try {
            setLoading(true);
            
            // Load hotel information
            if (!hotel) {
                const hotelResponse = await dataProvider.getOne('hotels', { id: hotelId });
                setHotel(hotelResponse.data);
            }

            // Load reference fields using the new endpoint
            const fieldsResponse = await dataProvider.getReferenceFields(hotelId);
            const fields = fieldsResponse.data?.referenceFields || [];
            
            // Sort by displayOrder
            const sortedFields = [...fields].sort((a, b) => a.displayOrder - b.displayOrder);
            
            setReferenceFields(sortedFields);
            setOriginalFields(sortedFields);
        } catch (error) {
            console.error('Error loading hotel fields data:', error);
            notify(translate('resources.hotels.fields.load_error'), { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    /**
     * Save all reference field changes
     */
    const handleSaveAll = async () => {
        if (!hotelId || !hasChanges || !permissions.canEdit) return;

        try {
            setSaving(true);
            
            // Update all fields using the new endpoint
            await dataProvider.updateReferenceFields(hotelId, referenceFields);
            
            // Update original state
            setOriginalFields([...referenceFields]);
            setHasChanges(false);
            
            notify(translate('resources.hotels.fields.saved_success'), { type: 'success' });
        } catch (error) {
            console.error('Error saving reference fields:', error);
            notify(translate('resources.hotels.fields.save_error'), { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    /**
     * Open dialog to create a new field
     */
    const handleAddField = () => {
        // Validate 5 fields limit
        if (referenceFields.length >= 5) {
            notify(translate('resources.hotels.fields.max_fields_warning'), { type: 'warning' });
            return;
        }

        const maxOrder = Math.max(...referenceFields.map(f => f.displayOrder || 0), -1);
        setFieldForm({
            key: '',
            label: '',
            isMandatory: false,
            displayOrder: maxOrder + 1,
        });
        setEditingFieldIndex(null);
        setFieldDialogOpen(true);
    };

    /**
     * Open dialog to edit an existing field
     */
    const handleEditField = (field: ReferenceFieldDto, index: number) => {
        setFieldForm({
            key: field.key,
            label: field.label,
            isMandatory: field.isMandatory,
            displayOrder: field.displayOrder,
        });
        setEditingFieldIndex(index);
        setFieldDialogOpen(true);
    };

    /**
     * Save a field (create or update)
     */
    const handleSaveField = () => {
        // Basic validations
        if (!fieldForm.label.trim()) {
            notify(translate('resources.hotels.fields.label_required'), { type: 'warning' });
            return;
        }

        if (!fieldForm.key.trim()) {
            notify(translate('resources.hotels.fields.key_required'), { type: 'warning' });
            return;
        }

        // Validate key format (lowercase letters, numbers and underscores only)
        if (!/^[a-z][a-z0-9_]*$/.test(fieldForm.key)) {
            notify(translate('resources.hotels.fields.key_format_error'), { type: 'warning' });
            return;
        }

        // Verify unique key
        const existingFieldIndex = referenceFields.findIndex(f => f.key === fieldForm.key);
        if (existingFieldIndex !== -1 && existingFieldIndex !== editingFieldIndex) {
            notify(translate('resources.hotels.fields.key_exists'), { type: 'warning' });
            return;
        }

        const newFields = [...referenceFields];
        
        if (editingFieldIndex !== null) {
            // Update existing field
            newFields[editingFieldIndex] = { ...fieldForm };
        } else {
            // Add new field
            newFields.push({ ...fieldForm });
        }

        // Reorder by displayOrder
        newFields.sort((a, b) => a.displayOrder - b.displayOrder);
        
        setReferenceFields(newFields);
        setFieldDialogOpen(false);
        setEditingFieldIndex(null);
    };

    /**
     * Confirm field deletion
     */
    const handleDeleteField = () => {
        if (fieldToDeleteIndex === null) return;

        const newFields = referenceFields.filter((_, index) => index !== fieldToDeleteIndex);
        
        // Readjust order of remaining fields
        newFields.forEach((field, index) => {
            field.displayOrder = index;
        });
        
        setReferenceFields(newFields);
        setDeleteDialogOpen(false);
        setFieldToDeleteIndex(null);
    };

    /**
     * Initiate field deletion
     */
    const handleDeleteClick = (index: number) => {
        if (!permissions.canDelete) {
            notify(translate('resources.hotels.fields.no_permissions_delete'), { type: 'warning' });
            return;
        }

        setFieldToDeleteIndex(index);
        setDeleteDialogOpen(true);
    };

    /**
     * Move field up in order
     */
    const handleMoveUp = (index: number) => {
        if (index === 0) return;
        
        const newFields = [...referenceFields];
        const temp = newFields[index - 1];
        newFields[index - 1] = newFields[index];
        newFields[index] = temp;
        
        // Update displayOrder
        newFields.forEach((field, idx) => {
            field.displayOrder = idx;
        });
        
        setReferenceFields(newFields);
    };

    /**
     * Move field down in order
     */
    const handleMoveDown = (index: number) => {
        if (index === referenceFields.length - 1) return;
        
        const newFields = [...referenceFields];
        const temp = newFields[index + 1];
        newFields[index + 1] = newFields[index];
        newFields[index] = temp;
        
        // Update displayOrder
        newFields.forEach((field, idx) => {
            field.displayOrder = idx;
        });
        
        setReferenceFields(newFields);
    };

    /**
     * Generate field key automatically
     */
    const generateFieldKey = (label: string): string => {
        return label.toLowerCase()
            .replace(/[áàäâ]/g, 'a')
            .replace(/[éèëê]/g, 'e')
            .replace(/[íìïî]/g, 'i')
            .replace(/[óòöô]/g, 'o')
            .replace(/[úùüû]/g, 'u')
            .replace(/ñ/g, 'n')
            .replace(/[^a-z0-9]/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_|_$/g, '');
    };

    /**
     * Handle label change to generate key automatically
     */
    const handleLabelChange = (value: string) => {
        setFieldForm(prev => ({
            ...prev,
            label: value,
            key: editingFieldIndex === null ? generateFieldKey(value) : prev.key,
        }));
    };

    /**
     * Cancel changes and restore original fields
     */
    const handleCancel = () => {
        setReferenceFields([...originalFields]);
        setHasChanges(false);
    };

   return (
        <MyFormContainer fullWidth={true}>
           
                <MyFormLayout>
                    <FormCard xs={12} variant="clean">
                        <MyFormSection
                            variant='outlined'
                            title={translate('resources.hotels.fields.title')}
                            subtitle={translate('resources.hotels.fields.subtitle')}
                            icon={<SettingsIcon />}
                        >
                           
                                    {/* Header section with count and add button */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, minHeight: 100 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {translate('resources.hotels.fields.count', { 
                                                current: referenceFields.length,
                                                max: 5 
                                            })}
                                        </Typography>
                                        
                                        {permissions.canEdit && referenceFields.length < 5 && (
                                            <Button
                                                variant="contained"
                                                startIcon={<AddIcon />}
                                                onClick={handleAddField}
                                                size="small"
                                            >
                                                {translate('resources.hotels.fields.add_field')}
                                            </Button>
                                        )}
                                    </Box>

                                    {/* Fields list */}
                                    {referenceFields.length === 0 ? (
                                        <Box 
                                            sx={{ 
                                                textAlign: 'center', 
                                                py: 6,
                                                px: 2,
                                                backgroundColor: 'grey.50',
                                                borderRadius: 2,
                                                border: '1px dashed',
                                                borderColor: 'grey.300'
                                            }}
                                        >
                                            <FieldIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                                            <Typography variant="h6" gutterBottom color="text.secondary">
                                                {translate('resources.hotels.fields.no_fields')}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" paragraph>
                                                {translate('resources.hotels.fields.no_fields_description')}
                                            </Typography>
                                            {permissions.canEdit && (
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<AddIcon />}
                                                    onClick={handleAddField}
                                                    sx={{ mt: 2 }}
                                                >
                                                    {translate('resources.hotels.fields.create_first_field')}
                                                </Button>
                                            )}
                                        </Box>
                                    ) : (
                                        <Box>
                                            {referenceFields.map((field, index) => (
                                                <FieldCard
                                                    key={field.key}
                                                    field={field}
                                                    index={index}
                                                    totalFields={referenceFields.length}
                                                    onEdit={handleEditField}
                                                    onDelete={handleDeleteClick}
                                                    onMoveUp={handleMoveUp}
                                                    onMoveDown={handleMoveDown}
                                                    canEdit={permissions.canEdit}
                                                    canDelete={permissions.canDelete}
                                                    translate={translate}
                                                />
                                            ))}
                                        </Box>
                                    )}

                                    {referenceFields.length >= 5 && (
                                        <Alert severity="warning" sx={{ mt: 2 }} icon={<WarningIcon />}>
                                            {translate('resources.hotels.fields.max_fields_reached')}
                                        </Alert>
                                    )}

                                    {/* Save/Cancel buttons when there are changes */}
                                    {hasChanges && (
                                        <>
                                            <Alert severity="info" sx={{ mt: 2 }}>
                                                {translate('resources.hotels.fields.unsaved_changes')}
                                            </Alert>
                                            
                                            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                                <Button
                                                    onClick={handleCancel}
                                                    disabled={saving}
                                                >
                                                    {translate('resources.hotels.fields.cancel')}
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleSaveAll}
                                                    disabled={saving || !permissions.canEdit}
                                                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                                                >
                                                    {saving 
                                                        ? translate('resources.hotels.fields.saving') 
                                                        : translate('resources.hotels.fields.save_changes')
                                                    }
                                                </Button>
                                            </Box>
                                        </>
                                    )}
                               
                        </MyFormSection>
                    </FormCard>
                </MyFormLayout>
        


            {/* Field dialog */}
            <Dialog 
                open={fieldDialogOpen} 
                onClose={() => !saving && setFieldDialogOpen(false)}
                maxWidth="sm" 
                fullWidth
            >
                <DialogTitle>
                    {editingFieldIndex !== null 
                        ? translate('resources.hotels.fields.edit_field') 
                        : translate('resources.hotels.fields.new_field')
                    }
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField
                            autoFocus
                            label={translate('resources.hotels.fields.field_label')}
                            value={fieldForm.label}
                            onChange={(e) => handleLabelChange(e.target.value)}
                            fullWidth
                            required
                            helperText={translate('resources.hotels.fields.label_helper')}
                        />

                        <TextField
                            label={translate('resources.hotels.fields.field_key')}
                            value={fieldForm.key}
                            onChange={(e) => setFieldForm(prev => ({ ...prev, key: e.target.value }))}
                            fullWidth
                            required
                            helperText={translate('resources.hotels.fields.key_helper')}
                            disabled={editingFieldIndex !== null}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={fieldForm.isMandatory}
                                    onChange={(e) => setFieldForm(prev => ({ ...prev, isMandatory: e.target.checked }))}
                                />
                            }
                            label={translate('resources.hotels.fields.field_mandatory')}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFieldDialogOpen(false)} disabled={saving}>
                        {translate('resources.hotels.fields.cancel')}
                    </Button>
                    <Button 
                        onClick={handleSaveField}
                        variant="contained"
                        disabled={saving || !fieldForm.label.trim() || !fieldForm.key.trim()}
                        startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                        {saving 
                            ? translate('resources.hotels.fields.saving') 
                            : (editingFieldIndex !== null 
                                ? translate('resources.hotels.fields.update') 
                                : translate('resources.hotels.fields.create'))
                        }
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete confirmation dialog */}
            <Dialog 
                open={deleteDialogOpen} 
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>{translate('resources.hotels.fields.delete_field')}</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {translate('resources.hotels.fields.delete_warning')}
                    </Alert>
                    <Typography>
                        {translate('resources.hotels.fields.delete_confirm', { 
                            name: fieldToDeleteIndex !== null ? referenceFields[fieldToDeleteIndex]?.label : '' 
                        })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        {translate('resources.hotels.fields.cancel')}
                    </Button>
                    <Button 
                        onClick={handleDeleteField} 
                        color="error" 
                        variant="contained"
                        disabled={saving}
                    >
                        {saving ? translate('resources.hotels.fields.deleting') : translate('resources.hotels.fields.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </MyFormContainer>
    );
};

export default HotelFields;